import { Box, HStack, Text, VStack, Badge, Spinner, Image } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'
import {
  Store, Package, Globe, LayoutGrid, Fingerprint, Boxes, Gavel,
  Briefcase, FolderTree, Contact, BookMarked, ClipboardList, Server,
  type LucideIcon,
} from 'lucide-react'
import { api } from '../../../../api/api'

const MotionBox = motion(Box)

// Registry of slash commands. Add an entry here and both the chat bar and this
// menu pick it up automatically: the trigger word, the search endpoint, the
// dropdown header, the prefix used when seeding an investigation, and a short
// hint shown in the `/` command palette.
//
// Only entity types with a REST list endpoint that supports `?search=` can be
// searched here. Graph-only node types (contact, registry, registrar, isp,
// subcase) are investigable by raw id but have no search picker yet.
interface SlashCommand {
  endpoint: string
  label: string
  seedPrefix: string
  hint: string
  icon: LucideIcon
  /** False for graph-only node types with no REST search endpoint yet. */
  searchable: boolean
}

export const SLASH_COMMANDS: Record<string, SlashCommand> = {
  seller:    { endpoint: '/sellers/',         label: 'Select seller',    seedPrefix: 'seller',    hint: 'Marketplace seller',        icon: Store,         searchable: true },
  item:      { endpoint: '/items/',           label: 'Select item',      seedPrefix: 'item',      hint: 'Listing / product item',    icon: Package,       searchable: true },
  site:      { endpoint: '/saw/',             label: 'Select site',      seedPrefix: 'saw',       hint: 'Standalone site',           icon: Globe,         searchable: true },
  saw:       { endpoint: '/saw/',             label: 'Select site',      seedPrefix: 'saw',       hint: 'Alias of /site',            icon: Globe,         searchable: true },
  platform:  { endpoint: '/platforms/',       label: 'Select platform',  seedPrefix: 'platform',  hint: 'Marketplace / platform',    icon: LayoutGrid,    searchable: true },
  entity:    { endpoint: '/kraken/entities/', label: 'Select entity',    seedPrefix: 'entity',    hint: 'Manual / custom entity',    icon: Fingerprint,   searchable: true },
  product:   { endpoint: '/kraken/products/', label: 'Select product',   seedPrefix: 'product',   hint: 'Custom product',            icon: Boxes,         searchable: true },
  seizure:   { endpoint: '/kraken/seizures/', label: 'Select seizure',   seedPrefix: 'seizure',   hint: 'Custom seizure',            icon: Gavel,         searchable: true },
  case:      { endpoint: '/cases/portal-search/', label: 'Select case',   seedPrefix: 'case',      hint: 'Parent enforcement case',   icon: Briefcase,     searchable: true },
  subcase:   { endpoint: '/cases/',           label: 'Select sub-case',  seedPrefix: 'subcase',   hint: 'Sub-case',                  icon: FolderTree,    searchable: false },
  contact:   { endpoint: '',                  label: 'Select contact',   seedPrefix: 'contact',   hint: 'Contact (graph node)',      icon: Contact,       searchable: false },
  registry:  { endpoint: '',                  label: 'Select registry',  seedPrefix: 'registry',  hint: 'Registry intermediary',     icon: BookMarked,    searchable: false },
  registrar: { endpoint: '',                  label: 'Select registrar', seedPrefix: 'registrar', hint: 'Registrar intermediary',    icon: ClipboardList, searchable: false },
  isp:       { endpoint: '',                  label: 'Select ISP',       seedPrefix: 'isp',       hint: 'ISP intermediary',          icon: Server,        searchable: false },
}

export type SlashCommandKey = string

// Canonical display order for the `/` command palette (all referenceable types).
export const SLASH_COMMAND_ORDER: SlashCommandKey[] = [
  'seller', 'item', 'site', 'platform', 'entity', 'product', 'seizure',
  'case', 'subcase', 'contact', 'registry', 'registrar', 'isp',
]

export interface SlashCommandResult {
  id: string
  title: string
  subtitle?: string
  image?: string | null
  tags: string[]
  type: SlashCommandKey
}

interface SlashCommandMenuProps {
  /** Active entity-search command (a full command word was typed). */
  command: SlashCommandKey | null
  searchQuery: string
  /** When non-null, the user is typing a bare `/<partial>` — show the palette. */
  paletteQuery?: string | null
  onSelect: (result: SlashCommandResult) => void
  /** Palette: user picked a command to start. */
  onSelectCommand?: (key: SlashCommandKey) => void
  onClose: () => void
}

const TAG_COLORS: Record<string, string> = {
  counterfeit: 'red',
  suspicious: 'orange',
  verified: 'green',
  takedown: 'purple',
  monitoring: 'blue',
}

// Extra intermediaries/relations to expand per type so the subtitle can show
// real names (host, platform, brand) instead of bare UUIDs. Endpoints that
// don't support a given expand simply ignore it.
const EXPAND_BY_CMD: Record<string, string> = {
  site: 'isps,registrar,registrant',
  saw: 'isps,registrar,registrant',
  seller: 'platform',
  item: 'brand,platform',
  product: 'brand',
}

const cap = (s: unknown): string => {
  const t = typeof s === 'string' ? s.trim() : ''
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : ''
}
// A related field may arrive as a bare id/string or an expanded object.
const nameOf = (v: any): string =>
  typeof v === 'string' ? '' : String(v?.name || v?.title || '').trim()
const hostOf = (u: unknown): string => {
  if (typeof u !== 'string' || !u) return ''
  try {
    return new URL(u).hostname.replace(/^www\./, '')
  } catch {
    return u.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
  }
}
const priceOf = (item: any): string =>
  item.price_eur != null ? `€${item.price_eur}` : item.price_usd != null ? `$${item.price_usd}` : ''

// The ordered, most-relevant fields to show as the secondary line, per type.
// Blank/duplicate parts are dropped downstream, so over-supplying is fine.
const subtitlePartsFor = (cmd: string, item: any): string[] => {
  const country = item.country ? String(item.country).toUpperCase() : ''
  const type = cap(typeof item.type === 'string' ? item.type : item.type?.name) || cap(item.seller_type)
  switch (cmd) {
    case 'entity':
      return [type, country, item.city || hostOf(item.url) || nameOf(item.brand)]
    case 'site':
    case 'saw':
      return [country, nameOf(item.isps?.[0]) || item.isp_data?.name || item.isp_data?.org || '', nameOf(item.registrar)]
    case 'seller':
      return [type, nameOf(item.platform), country]
    case 'item':
      return [nameOf(item.brand), nameOf(item.platform), priceOf(item) || country]
    case 'platform':
      return [item.domain || hostOf(item.url), country]
    case 'product':
      return [cap(item.product_type), nameOf(item.brand), priceOf(item)]
    case 'seizure':
      return [country, item.city, item.value != null ? String(item.value) : '']
    case 'case':
    case 'subcase':
      // MemberPortal cases identify by their number (title); the subtitle
      // carries the status. The number is deduped out of the subtitle
      // downstream when it already equals the title.
      return [cap(item.status)]
    default:
      return [type, country]
  }
}

// Compose up to three distinct, non-title parts into "Person · NL · Cloudflare".
const buildSubtitle = (cmd: string, item: any, title: string): string => {
  const seen = new Set<string>()
  const parts = subtitlePartsFor(cmd, item)
    .map((p) => (p == null ? '' : String(p)).trim())
    .filter((p) => {
      const k = p.toLowerCase()
      if (!p || k === title.toLowerCase() || seen.has(k)) return false
      seen.add(k)
      return true
    })
    .slice(0, 3)
  return parts.join(' · ')
}

const PanelShell = ({ children }: { children: React.ReactNode }) => (
  <AnimatePresence>
    <MotionBox
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.15 }}
      position="absolute"
      bottom="100%"
      left={0}
      right={0}
      mb={2}
      mx={4}
      bg="white"
      borderRadius="12px"
      boxShadow="0 4px 24px rgba(0,0,0,0.12)"
      border="1px solid"
      borderColor="gray.100"
      maxH="300px"
      overflowY="auto"
      zIndex={20}
    >
      {children}
    </MotionBox>
  </AnimatePresence>
)

const SlashCommandMenu = ({
  command,
  searchQuery,
  paletteQuery,
  onSelect,
  onSelectCommand,
  onClose,
}: SlashCommandMenuProps) => {
  const [results, setResults] = useState<SlashCommandResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  // --- Palette mode: list the commands themselves, filtered by the partial. ---
  const paletteItems =
    paletteQuery != null
      ? SLASH_COMMAND_ORDER.filter((k) => k.startsWith(paletteQuery.toLowerCase()))
      : []

  // --- Entity-search mode: query the command's endpoint. ---
  useEffect(() => {
    if (!command) return
    const cmd = SLASH_COMMANDS[command]
    setSelectedIndex(0)
    // Graph-only types (no REST endpoint) can't be searched — clear and bail.
    if (!cmd?.searchable || !cmd.endpoint) {
      setResults([])
      setIsLoading(false)
      return
    }
    setIsLoading(true)

    const endpoint = cmd.endpoint
    const params: Record<string, string> = { page_size: '10' }
    if (searchQuery) params.search = searchQuery
    // Expand related intermediaries (host, registrar, platform, brand) so the
    // subtitle can show real names instead of a meaningless UUID.
    if (EXPAND_BY_CMD[command]) params.expand = EXPAND_BY_CMD[command]

    const timer = setTimeout(() => {
      api
        .get(endpoint, { params })
        .then((res) => {
          const raw = res.data?.data || res.data?.results || (Array.isArray(res.data) ? res.data : [])
          const list = Array.isArray(raw) ? raw : []
          const isCase = command === 'case' || command === 'subcase'
          const mapped: SlashCommandResult[] = list.map((item: any) => {
            // MemberPortal cases identify by their number (e.g. 26-958511-CN);
            // other types keep their existing title precedence.
            const title = isCase
              ? item.case_number || item.label || String(item.id)
              : item.title || item.name || item.domain || item.url || item.reference ||
                item.case_number || item.label || String(item.id)
            // Compose a relevant secondary line ("Person · NL · Cloudflare").
            // Never fall back to a UUID — use the type hint when we have nothing.
            // Guarded so one odd record can't blank the whole picker.
            let subtitle = cmd.hint || ''
            try {
              subtitle = buildSubtitle(command, item, title) || cmd.hint || ''
            } catch {
              /* keep the hint fallback */
            }
            const image =
              item.logo || item.thumbnail || item.image || item.favicon || item.icon ||
              item.platform?.logo || item.seller?.logo || null
            return {
              id: String(item.id),
              title,
              subtitle,
              image,
              tags: [item.group_tag, item.group_tag_2, item.group_tag_3, item.group_tag_4, item.group_tag_5, item.status].filter(Boolean),
              type: command,
            }
          })
          setResults(mapped)
        })
        .catch(() => setResults([]))
        .finally(() => setIsLoading(false))
    }, 300)

    return () => clearTimeout(timer)
  }, [command, searchQuery])

  useEffect(() => {
    setSelectedIndex(0)
  }, [paletteQuery])

  // Keyboard nav for both modes.
  useEffect(() => {
    const active = command !== null || paletteQuery != null
    const list = command !== null ? results : paletteItems
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!active) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, list.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter' && list.length > 0) {
        e.preventDefault()
        if (command !== null) onSelect(results[selectedIndex])
        else onSelectCommand?.(paletteItems[selectedIndex])
      } else if (e.key === 'Tab' && paletteQuery != null && paletteItems.length > 0) {
        e.preventDefault()
        onSelectCommand?.(paletteItems[selectedIndex])
      } else if (paletteQuery != null && /^[1-9]$/.test(e.key)) {
        // Number shortcuts: press 1–9 to pick that command from the palette.
        const idx = parseInt(e.key, 10) - 1
        if (idx < paletteItems.length) {
          e.preventDefault()
          onSelectCommand?.(paletteItems[idx])
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [command, paletteQuery, results, paletteItems.length, selectedIndex, onSelect, onSelectCommand, onClose])

  // ── Palette mode ──────────────────────────────────────────────────────────
  if (paletteQuery != null) {
    const onSelectCmd = onSelectCommand ?? (() => {})
    return (
      <PanelShell>
        <Box px={3} py={2} borderBottom="1px solid" borderColor="gray.50">
          <Text fontSize="11px" fontWeight={600} color="gray.400" textTransform="uppercase" letterSpacing="0.05em">
            Commands
          </Text>
        </Box>
        {paletteItems.length === 0 ? (
          <Box px={3} py={3}>
            <Text fontSize="13px" color="gray.400">No matching command</Text>
          </Box>
        ) : (
          <VStack align="stretch" gap={0} py={1}>
            {paletteItems.map((key, index) => {
              const cmd = SLASH_COMMANDS[key]
              const Icon = cmd.icon
              return (
                <HStack
                  key={key}
                  px={3}
                  py={2}
                  cursor="pointer"
                  bg={index === selectedIndex ? 'purple.50' : 'transparent'}
                  _hover={{ bg: 'purple.50' }}
                  onClick={() => onSelectCmd(key)}
                  gap={2.5}
                >
                  <Box
                    w="26px"
                    h="26px"
                    borderRadius="8px"
                    bg="purple.50"
                    color="purple.500"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Box as={Icon} w="15px" h="15px" strokeWidth={2.2} />
                  </Box>
                  <Text fontSize="13px" fontWeight={600} color="purple.600" fontFamily="mono" minW="74px">
                    /{key}
                  </Text>
                  <Text fontSize="12px" color="gray.500" flex={1} lineClamp={1}>
                    {cmd.hint}
                  </Text>
                  {index < 9 && (
                    <Box
                      flexShrink={0}
                      minW="18px"
                      h="18px"
                      px="4px"
                      borderRadius="5px"
                      bg="gray.100"
                      color="gray.500"
                      fontSize="11px"
                      fontWeight={600}
                      fontFamily="mono"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {index + 1}
                    </Box>
                  )}
                </HStack>
              )
            })}
          </VStack>
        )}
      </PanelShell>
    )
  }

  // ── Entity-search mode ────────────────────────────────────────────────────
  if (!command) return null

  return (
    <PanelShell>
      <Box px={3} py={2} borderBottom="1px solid" borderColor="gray.50">
        <Text fontSize="11px" fontWeight={600} color="gray.400" textTransform="uppercase" letterSpacing="0.05em">
          {SLASH_COMMANDS[command].label}
        </Text>
      </Box>

      {isLoading ? (
        <Box px={3} py={4} textAlign="center">
          <Spinner size="sm" color="gray.400" />
        </Box>
      ) : results.length === 0 ? (
        <Box px={3} py={3}>
          <Text fontSize="13px" color="gray.400">
            {!SLASH_COMMANDS[command]?.searchable
              ? `Search not available — reference by id, e.g. ${SLASH_COMMANDS[command]?.seedPrefix}_<id>`
              : searchQuery
                ? 'No results found'
                : 'Type to search…'}
          </Text>
        </Box>
      ) : (
        <VStack align="stretch" gap={0} py={1}>
          {results.map((result, index) => {
            const Icon = SLASH_COMMANDS[command]?.icon
            const iconAvatar = (
              <Box
                w="100%"
                h="100%"
                bg="purple.50"
                color="purple.500"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {Icon && <Box as={Icon} w="16px" h="16px" strokeWidth={2.2} />}
              </Box>
            )
            return (
              <HStack
                key={result.id}
                px={3}
                py={2}
                cursor="pointer"
                bg={index === selectedIndex ? 'gray.50' : 'transparent'}
                _hover={{ bg: 'gray.50' }}
                onClick={() => onSelect(result)}
                gap={2.5}
              >
                {/* Square avatar — real logo/thumbnail, else the type's icon. */}
                <Box
                  w="32px"
                  h="32px"
                  borderRadius="8px"
                  overflow="hidden"
                  flexShrink={0}
                  border="1px solid"
                  borderColor="gray.100"
                >
                  {result.image ? (
                    <Image
                      src={result.image}
                      alt={result.title}
                      w="100%"
                      h="100%"
                      objectFit="cover"
                      fallback={iconAvatar}
                    />
                  ) : (
                    iconAvatar
                  )}
                </Box>

                <VStack align="start" gap={0} flex={1} minW={0}>
                  <Text fontSize="13px" fontWeight={500} color="gray.700" lineClamp={1}>
                    {result.title}
                  </Text>
                  {result.subtitle && (
                    <Text fontSize="11px" color="gray.400" lineClamp={1}>
                      {result.subtitle}
                    </Text>
                  )}
                </VStack>

                <HStack gap={1} flexShrink={0} flexWrap="wrap" justify="end" maxW="120px">
                  {result.tags.map((tag) => (
                    <Badge
                      key={tag}
                      size="sm"
                      colorScheme={TAG_COLORS[String(tag).toLowerCase()] || 'gray'}
                      fontSize="10px"
                      fontWeight={500}
                    >
                      {tag}
                    </Badge>
                  ))}
                </HStack>
              </HStack>
            )
          })}
        </VStack>
      )}
    </PanelShell>
  )
}

export default SlashCommandMenu
